import { useState, useEffect } from 'react';
import { useQueryClient } from 'shared-ui';
import { toast } from 'react-toastify';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { returnDateMultiCards } from 'api/cards/cards';
import { QueryKeyEnum } from 'core/global.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { useSelectedCard } from 'hooks/useSelectedCard/useSelectedCard';
import { ReturnDateCardBody } from 'api/cards/cards.types';
import { useMutation } from 'hooks/useMutation/useMutation';
import { getEndOfToday } from 'helpers/getEndOfToday';
import { useModalState } from 'hooks/useModalState/useModalState';

import { ReturnDateMultiCards } from './ReturnDateMultiCards';
import { ReturnDateMultiCardsContainerProps } from './ReturnDateMultiCards.types';
import { useReturnDateMultiCardsValidation } from './ReturnDateMultiCards.validation';

export const ReturnDateMultiCardsContainer = ({ checkedCards }: ReturnDateMultiCardsContainerProps) => {
  const queryClient = useQueryClient();
  const { formatMessage } = useLocale();
  const { selectedCard, setSelectedCard } = useSelectedCard();
  const { isModalOpen, onModalOpen, onModalClose } = useModalState();

  const [minDate, setMinDate] = useState(new Date());

  const { mutate: onReturnDateMultiCards, isLoading } = useMutation(returnDateMultiCards, {
    onSuccess: (_, { body }) => {
      const returnDate = formMethods.watch('dataZwrotu');

      if (selectedCard && checkedCards.some(({ id }) => id === selectedCard?.id)) {
        setSelectedCard({ ...selectedCard, ...body, dataZwrotu: returnDate });
      }

      queryClient.invalidateQueries([QueryKeyEnum.CARDS]);

      toast.dark(formatMessage({ id: AppMessages['cards.multiAction.returnDate.success'] }));

      onModalClose();
    },
  });

  const { schema } = useReturnDateMultiCardsValidation(minDate);
  const formMethods = useForm<ReturnDateCardBody>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      dataZwrotu: getEndOfToday(),
    },
  });

  useEffect(() => {
    setMinDate(new Date());
    formMethods.reset({ dataZwrotu: getEndOfToday() });
  }, [isModalOpen]);

  const onSubmit = (formBody: ReturnDateCardBody) => {
    onReturnDateMultiCards({ body: { ...formBody, cards: checkedCards.map(({ id }) => id) } });
  };

  return (
    <FormProvider {...formMethods}>
      <ReturnDateMultiCards
        checkedCards={checkedCards}
        isModalOpen={isModalOpen}
        onModalClose={onModalClose}
        onModalOpen={onModalOpen}
        isLoading={isLoading}
        onSubmit={onSubmit}
        minDate={minDate}
      />
    </FormProvider>
  );
};
